






































































































import { Vue, Component, Prop, Provide, Watch } from 'vue-property-decorator';
import _debounce from 'lodash/debounce';
import { SwExtendedVue } from '../sw-extended-vue';
import { domService } from '../dom-service';
import { SwIcon } from '../sw-icon';
import SwTableHeader from './sw-table-header.vue';
import SwTableBody from './sw-table-body.vue';
import SwTableFooter from './sw-table-footer.vue';
import SwTablePagination from './sw-table-pagination.vue';
import SwTableFixedPortion from './sw-table-fixed-portion.vue';
import SwTableColResizer from './sw-table-col-resizer.vue';
import { utilService, EVENT_TYPE } from './util.service';
import {
  TableColumnConfig,
  TableEntry,
  SortedColumnInfo,
  TableFooterConfig,
  TableBodyMetrics,
  TableHeaderMetrics,
  ColumnResizerTrigger,
  FixedColumnsConfigDescriptor,
  PaginationConfig,
  TableEntrySelectingEventData,
  TableEntryExpandingEventData,
  ColumnSortRequestData,
  ClaimedColumnResizerTrigger,
  TableColumnRenderFunc,
  TableRowExpandMode,
  TableEntryFloatActionsGenerator,
  PageNumberChangeRequestData,
  PageSizeChangeRequestData,
  TableRowSelectionMode,
  InfiniteScrollConfig,
  TableEntryMoveRequestData,
  TableData,
  TableEntryGroup,
  GroupCollapseRequestData,
  FloatActionPlacement,
  TableCellClickEventData,
  InternalExpandEventData,
  ColumnsAdjustRequestData,
} from './types';
import { CSSStyleOptions } from '../common-types';
// import { OverflowEvent } from '../sw-scroll-view';

/**
 * Events:
 * @event request-sort `(ColumnSortRequestData)`
 * @event request-adjust-columns `(ColumnsAdjustRequestData)` this will send all columns that need to be adjusted against the `columnsConfig`
 * @event request-change-page-number `(PageNumberChangeRequestData)`
 * @event request-change-page-size `(PageSizeChangeRequestData)`
 * @event request-load-more `(minNumToLoad: number)`
 * @event request-move-entry `(TableEntryMoveRequestData)`
 * @event entry-expanding `(TableEntryExpandingEventData)`
 * @event entry-selecting `(TableEntrySelectingEventData)`
 * @event request-group-collapse `(GroupCollapseRequestData)`
 * @event cell-click `(TableCellClickEventData)`
 */
@Component({
  components: {
    SwIcon,
    SwTableHeader,
    SwTableBody,
    SwTableFooter,
    SwTablePagination,
    SwTableFixedPortion,
    SwTableColResizer,
  },
})
export default class SwTable extends SwExtendedVue {
  /** Array to define table columns */
  @Prop({ type: Array, required: true })
  columnsConfig: TableColumnConfig[];

  @Prop({ type: Array, required: true })
  tableData: TableData;

  @Prop({ type: Boolean, default: false })
  columnAdjustable: boolean;

  @Prop({ type: Object, default: null })
  adjustedColumns: ColumnsAdjustRequestData | null;

  @Prop({ type: String, default: 'in-row' })
  expandMode: TableRowExpandMode;

  /** accordion behavior for expandable rows, hence only effective when there are expandable rows and it is `in-row` mode */
  @Prop({ type: Boolean, default: false })
  accordion: boolean;

  /** standalone render function when it is `slide` expand mode, hence no `expand` column in the `columnsConfig` */
  @Prop({ type: Function })
  expandRender: TableColumnRenderFunc;

  /** the no of columns that are visible (from the left) when `slide` expand is expanded */
  @Prop({ type: Number, default: 2 })
  columnsToShowSlideExpand: number;

  /** effective only if `expandMode === 'slide'`, it propagates expandion change to selection, in other words, selection
   * will become single-selection in this case.
   */
  @Prop({ type: Boolean, default: true })
  syncSlideExpandToSelection: boolean;

  /** for now, only support single sorted column */
  @Prop({ type: Array, default() { return []; } })
  sortedColumns: SortedColumnInfo[];

  @Prop({ type: Array, required: false })
  initSelected: string[] | undefined;

  /** this is NOT a reactive property */
  @Prop({ type: String, default: 'free' })
  selectionMode: TableRowSelectionMode;

  @Prop({ type: String, default: 'light' })
  theme: 'light' | 'dark';

  @Prop({ type: String, default: 'auto' })
  size: 'normal' | 'compact' | 'auto';

  @Prop({ type: Boolean, default: false })
  stripe: boolean;

  @Prop({ type: Boolean, default: false })
  border: boolean;

  @Prop({ default: null, type: Object })
  footerConfig: TableFooterConfig | null;

  @Prop({ default: null, type: Object })
  pagination: PaginationConfig | null;

  @Prop({ default: null, type: Object })
  infiniteScroll: InfiniteScrollConfig | null;

  @Prop({ type: Boolean, default: true })
  showHeader: boolean;

  @Prop({ type: Number, default: 0 })
  nestLevel: number;

  /** valid only when nested === true */
  @Prop({ type: Number, default: 20 })
  indentSize: number;

  @Prop({ type: Boolean, default: false })
  noBackground: boolean;

  @Prop({ type: Function })
  getFloatActions: TableEntryFloatActionsGenerator;

  @Prop({ type: Boolean, default: false })
  clickRowToExpand: boolean;

  @Prop({ type: Boolean, default: true })
  clickRowToSelect: boolean;

  @Prop({ type: Boolean, default: false })
  isLoadingData: boolean;

  @Prop({ type: Boolean, default: false })
  rowDraggable: boolean;

  @Prop({ type: Array, default() { return []; } })
  collapsedGroups: string[];

  @Prop({ type: Boolean, default: false })
  scrollbarAutoHide: boolean;

  @Prop({ type: String, default: 'end' })
  floatActionPosition: FloatActionPlacement;

  @Prop({ type: Boolean, default: false })
  holdingSelecting: boolean;

  instanceId: string = String(Math.floor(Math.random() * 10000000000));

  selectedSet: Set<string> = new Set();
  shadowSelectedArr: string[] = [];
  mostRecentRegularSelectedEntry: TableEntry | null = null;
  expandedSet: Set<string> = new Set();
  shadowExpandedArr: string[] = [];
  bodyMetrics: TableBodyMetrics = {
    left: null,
    top: null,
    width: null,
    clientWidth: null,
    height: null,
    clientHeight: null,
  };
  headerMetrics: TableHeaderMetrics = {
    clientHeight: null,
  };
  headerObserver: MutationObserver | null = null;
  showResizer = false;
  resizerTrigger: ColumnResizerTrigger | null = null;
  isTriggerLocked = false;

  private internalRequestLoadMoreMinNumRecalcCount: number = 0;

  // get layeredColumnsConfig(): ColumnLayer[] {
  //   function getLayersFromColumnConfig(col: TableColumnConfig) {
  //     const layers: ColumnLayer[] = [];
  //     const childrenLayersArr: ColumnLayer[][] = [];

  //     if (!Array.isArray(col.children)) {
  //       return [[{ col, colspan: 1, rowspan: 1 }]];
  //     }

  //     col.children.forEach((childCol) => {
  //       childrenLayersArr.push(getLayersFromColumnConfig(childCol));
  //     });

  //     const maxLayerNum = childrenLayersArr.reduce((max, curLayers) => (Math.max(max, curLayers.length)), 0);
  //     const totalColspan = childrenLayersArr.reduce((acc, curLayers) => (acc + curLayers[0][0].colspan), 0);

  //     childrenLayersArr.reduce(
  //       (lyrs, curLayers) => {
  //         const toLayers = lyrs;

  //         if (maxLayerNum > curLayers.length) {
  //           /** adjust the rowspan of elements of each layer */
  //           const diff = maxLayerNum - curLayers.length;
  //           curLayers.forEach((layer) => {
  //             layer.forEach((le) => {
  //               if (Array.isArray(le.col.children)) { return; }
  //               const lee = le;
  //               lee.rowspan += diff;
  //             });
  //           });
  //         }

  //         for (let i = 0; i < maxLayerNum; i += 1) {
  //           if (!Array.isArray(toLayers[i])) {
  //             toLayers[i] = [];
  //           }
  //           if (Array.isArray(curLayers[i])) {
  //             toLayers[i] = toLayers[i].concat(curLayers[i]);
  //           }
  //         }

  //         return lyrs;
  //       },
  //       layers,
  //     );

  //     layers.unshift([{ col, colspan: totalColspan, rowspan: 1 }]);

  //     return layers;
  //   }

  //   const topLayers = getLayersFromColumnConfig({
  //     key: '',
  //     children: this.columnsConfig,
  //   } as TableColumnConfig);

  //   return topLayers.slice(1);
  // }

  get adjustedColumnsConfig(): TableColumnConfig[] {
    const adjustedColumns = this.adjustedColumns;
    if (!adjustedColumns) { return this.columnsConfig; }

    function adjustCol(col: TableColumnConfig): TableColumnConfig {
      const copy = Object.assign({}, col);
      if (copy.children) {
        copy.children = copy.children.map(adjustCol);
      } else {
        if (adjustedColumns![copy.key] !== undefined) {
          copy.width = adjustedColumns![copy.key];
          delete copy.scalable;
        }
      }
      return copy;
    }
    return this.columnsConfig.map<TableColumnConfig>(adjustCol);
  }

  /** columns that are as leaves hence have no children */
  get effectiveColumnsConfig(): TableColumnConfig[] {
    function getLeaves(col: TableColumnConfig) {
      const leaves: TableColumnConfig[] = [];
      if (!Array.isArray(col.children)) {
        return [col];
      }
      col.children.forEach((childCol) => {
        Array.prototype.push.apply(leaves, getLeaves(childCol));
      });
      return leaves;
    }

    return getLeaves({
      key: '',
      children: this.adjustedColumnsConfig,
    });
  }

  get fixedColumnsConfig(): FixedColumnsConfigDescriptor {
    const fixedCols: FixedColumnsConfigDescriptor = {
      left: null,
      right: null,
    };
    let i = 0;

    for (i = 0; i < this.adjustedColumnsConfig.length; i += 1) {
      if (this.adjustedColumnsConfig[i].fixed !== true) { break; }
      /** fixed column must have width specified explicitly */
      if (!this.adjustedColumnsConfig[i].type && typeof this.adjustedColumnsConfig[i].width !== 'number') {
        throw new Error('width for fixed column is not specified properly');
      }
      if (fixedCols.left) {
        fixedCols.left.push(this.adjustedColumnsConfig[i]);
      } else {
        fixedCols.left = [this.adjustedColumnsConfig[i]];
      }
    }

    for (i = this.adjustedColumnsConfig.length - 1; i >= 0; i -= 1) {
      if (this.adjustedColumnsConfig[i].fixed !== true) { break; }
      /** fixed column must have width specified explicitly */
      if (!this.adjustedColumnsConfig[i].type && typeof this.adjustedColumnsConfig[i].width !== 'number') {
        throw new Error('width for fixed column is not specified properly');
      }
      if (fixedCols.right) {
        fixedCols.right.unshift(this.adjustedColumnsConfig[i]);
      } else {
        fixedCols.right = [this.adjustedColumnsConfig[i]];
      }
    }

    return fixedCols;
  }

  /** Sorted copy of original data array */
  get sortedData(): TableData {
    const sortedData = this.tableData.slice(0);
    /** for now, support single sorted column */
    if (this.sortedColumns.length > 0) {
      const sortCol = this.effectiveColumnsConfig.find(col => (col.key === this.sortedColumns[0].colKey));
      if (sortCol && typeof sortCol.sorter === 'function') {
        if (utilService.isTableEntryArray(sortedData)) {
          sortedData.sort(sortCol.sorter);
        } else {
          /** do sorting for each group respectively */
          for (const group of sortedData) {
            group.entries.sort(sortCol.sorter);
          }
        }
      } else if (sortCol && typeof sortCol.sorter === 'boolean') {
        if (sortCol.sorter && sortCol.dataKey) {
          if (utilService.isTableEntryArray(sortedData)) {
            sortedData.sort(utilService.genDefaultSortFunc(sortCol.dataKey));
          } else {
            for (const group of sortedData) {
              group.entries.sort(utilService.genDefaultSortFunc(sortCol.dataKey));
            }
          }
        } else {}
      }
      /** sorting direction */
      if (sortCol && sortCol.sorter !== false) {
        if (!this.sortedColumns[0].ascending) {
          sortedData.reverse();
        }
      }
    }
    return Object.freeze(sortedData) as TableData;
  }

  /** Either paged copy, or just sorted copy */
  get effectiveData(): TableData {
    if (!this.showPagination || !this.effectivePaginationConfig) {
      return this.sortedData;
    }

    /** // FIXME: the case for TableEntryGroup[] */
    const absStart = Math.max(this.effectivePaginationConfig!.pageNumber - 1, 0) * this.effectivePaginationConfig!.pageSize;
    const start = Math.max(absStart - this.effectivePaginationConfig.datasetOffset!, 0);
    const end = start + this.effectivePaginationConfig!.pageSize;
    return Object.freeze(this.sortedData.slice(start, end)) as TableData;
  }

  get effectiveDataEntryKeySet() {
    const keySet: Set<string> = new Set();
    utilService.traverseTableData(this.effectiveData, (entry) => {
      keySet.add(entry._key);
    });
    return keySet;
  }

  get selectedStatus() {
    /** NOTE: seleced status is specific to effective dataset */
    if (this.shadowSelectedArr.length <= 0) { return 'none'; }

    let isAllSelected = false;
    if (utilService.isTableEntryArray(this.effectiveData)) {
      isAllSelected = this.effectiveData.every(e => (e._selectable === false || this.selectedSet.has(e._key)));
    } else {
      isAllSelected = this.effectiveData.every((g) => {
        return g.entries.every(e => (e._selectable === false || this.selectedSet.has(e._key)));
      });
    }

    return isAllSelected ? 'all' : 'partial';
  }

  get headerProps() {
    return {
      columnsConfig: this.adjustedColumnsConfig,
      sortedColumns: this.sortedColumns,
      theme: this.theme,
      size: this.size,
      border: this.border,
      effectiveColumnsConfig: this.effectiveColumnsConfig,
      columnAdjustable: this.effectiveColumnAdjustable,
      bodyMetrics: this.bodyMetrics,
      selectedStatus: this.selectedStatus,
    };
  }

  get bodyProps() {
    return {
      instanceId: this.instanceId,
      columnsConfig: this.adjustedColumnsConfig,
      effectiveColumnsConfig: this.effectiveColumnsConfig,
      tableData: this.effectiveData,
      expandMode: this.expandMode,
      accordion: this.accordion,
      expandRender: this.expandRender,
      columnsToShowSlideExpand: this.columnsToShowSlideExpand,
      sortedColumns: this.sortedColumns,
      theme: this.theme,
      size: this.size,
      stripe: this.stripe,
      border: this.border,
      nestLevel: this.nestLevel,
      indentSize: this.indentSize,
      noBackground: this.noBackground,
      selectedArr: this.shadowSelectedArr,
      expandedArr: this.shadowExpandedArr,
      effectivePaginationConfig: this.effectivePaginationConfig,
      bodyMetrics: this.bodyMetrics,
      getFloatActions: this.getFloatActions,
      clickRowToExpand: this.clickRowToExpand,
      clickRowToSelect: this.clickRowToSelect,
      isLoadingData: this.isLoadingData,
      rowDraggable: this.rowDraggable,
      collapsedGroups: this.collapsedGroups,
      scrollbarAutoHide: this.scrollbarAutoHide,
      floatActionPosition: this.floatActionPosition,
      holdingSelecting: this.effectiveHoldingSelecting,
    };
  }

  get footerProps() {
    return {
      footerConfig: this.footerConfig,
      columnsConfig: this.adjustedColumnsConfig,
      // effectiveColumnsConfig: this.effectiveColumnsConfig,
      effectiveInfiniteScrollConfig: this.effectiveInfiniteScrollConfig,
      tableData: this.effectiveData,
      theme: this.theme,
      size: this.size,
      border: this.border,
      bodyMetrics: this.bodyMetrics,
    };
  }

  get showFixedLeft() {
    return !!this.fixedColumnsConfig.left;
  }

  get showFixedRight() {
    return !!this.fixedColumnsConfig.right;
  }

  get showFooter() {
    return this.footerConfig !== null;
  }

  get showPagination() {
    /** we take infinite scroll over pagination */
    return !this.useInfiniteScroll && this.pagination !== null;
  }

  get useInfiniteScroll() {
    return this.infiniteScroll !== null;
  }

  /** make up every default value for pagination config */
  // FIXME: the case for TableEntryGroup[]
  get effectivePaginationConfig(): PaginationConfig | null {
    if (!this.showPagination) {
      return null;
    }
    const mergedConfig: PaginationConfig = Object.assign(
      {
        pageNumber: 1,
        pageSize: 10,
        pageSizeOpts: [10, 20, 50],
        datasetOffset: 0,
        datasetTotal: this.tableData.length,
        placement: 'bottom',
        nestedPagination: null,
      } as PaginationConfig,
      this.pagination,
    );

    if (mergedConfig.nestedPagination !== null) {
      mergedConfig.nestedPagination = Object.assign(
        /* tslint:disable:ter-indent */
        {
          pageNumber: 1,
          pageSize: 10,
          pageSizeOpts: [10, 20, 50],
          placement: 'bottom',
          nestedPagination: null,
        } as PaginationConfig,
        /* tslint:enable:ter-indent */
        mergedConfig.nestedPagination,
      );
    }

    /** correct dataset total */
    mergedConfig.datasetTotal = Math.max(mergedConfig.datasetTotal!, mergedConfig.datasetOffset! + this.tableData.length);

    return mergedConfig;
  }

  get effectiveInfiniteScrollConfig(): InfiniteScrollConfig | null {
    if (!this.useInfiniteScroll) { return null; }
    const mergedConfig: InfiniteScrollConfig = Object.assign(
      {} as InfiniteScrollConfig,
      this.infiniteScroll,
    );
    return mergedConfig;
  }

  get effectiveColumnAdjustable() {
    return (this.columnAdjustable);
  }

  get effectiveHoldingSelecting() {
    return this.clickRowToSelect && this.holdingSelecting && this.selectionMode !== 'single';
  }

  get isHeaderFixed() {
    return true;
  }

  get useOuterBorder() {
    return this.nestLevel === 0 && this.border;
  }

  get wrapperStyles(): CSSStyleOptions {
    const styles: CSSStyleOptions = {};
    return styles;
  }

  get requestLoadMoreMinNum(): number {
    if (!this.useInfiniteScroll) { this.internalRequestLoadMoreMinNumRecalcCount += 1; return -1; }
    if (this.bodyMetrics.height === null || this.bodyMetrics.clientHeight === null || this.bodyMetrics.top === null) {
      this.internalRequestLoadMoreMinNumRecalcCount += 1;
      return -1;
    }
    if (this.isLoadingData) { this.internalRequestLoadMoreMinNumRecalcCount += 1; return -1; }

    const margin = 300;
    const expectedHeight = this.bodyMetrics.top + this.bodyMetrics.clientHeight + margin;
    const currHeight = this.tableData.length === 0 ? 0 : this.bodyMetrics.height;
    const isLessThanExpectedHeight = currHeight < expectedHeight;
    if (!isLessThanExpectedHeight) { this.internalRequestLoadMoreMinNumRecalcCount += 1; return -1; }

    const totalEntryCount = utilService.getTableDataEntryLength(this.tableData);
    const remaining = this.effectiveInfiniteScrollConfig!.datasetTotal === undefined ? Infinity :
      (this.effectiveInfiniteScrollConfig!.datasetTotal - totalEntryCount);
    if (remaining <= 0) { this.internalRequestLoadMoreMinNumRecalcCount += 1; return -1; }

    /** NOTE: this has to be according to the height of `sw-table-row` */
    const rowHeight = this.size === 'normal' ? 42 : 28;
    this.internalRequestLoadMoreMinNumRecalcCount += 1;
    return Math.min(Math.ceil((expectedHeight - currHeight) / rowHeight), remaining);
  }

  public getAllSelectedKeys(): string[] {
    return this.shadowSelectedArr.slice(0);
  }

  public getAllExpandedKeys(): string[] {
    return this.shadowExpandedArr.slice(0);
  }

  @Provide()
  public claimEntrySelected(entry: TableEntry, selected: boolean, finalize: boolean = true, event: MouseEvent | null = null) {
    if (!this.effectiveDataEntryKeySet.has(entry._key)) { return; }
    if (entry._selectable === false && selected) {
      // console.warn(`Tried to select entry [${entry._key}] not selectable, which is not allowed...`);
      return;
    }

    if (event && event.shiftKey && selected && !this.claimMostRecentRegularSelectedEntry(entry, event)) {
      this.claimEntrySelectedRangeEnd(entry);
      return;
    }

    let doEmit = true;
    if (selected) {
      if (this.selectionMode === 'single') {
        /** unselect all other entries */
        if (this.selectedSet.size > 0) {
          Array.from(this.selectedSet)
            .forEach((k) => {
              if (k !== entry._key) {
                this.selectedSet.delete(k);
                this.$emit('entry-selecting', { entryKey: k, selecting: false } as TableEntrySelectingEventData);
              }
            });
        }
      }
      doEmit = !this.selectedSet.has(entry._key);
      this.selectedSet.add(entry._key);
    } else {
      if (this.selectionMode === 'single') {
        /** don't allow selecting nothing */
        if (this.selectedSet.size === 1) { return; }
      }
      this.selectedSet.delete(entry._key);
    }
    if (doEmit) {
      this.$emit('entry-selecting', { entryKey: entry._key, selecting: selected } as TableEntrySelectingEventData);
    }
    if (finalize) {
      this.shadowSelectedArr = Array.from(this.selectedSet);
      if (selected) {
        this.claimMostRecentRegularSelectedEntry(entry, event);
      }
    }
  }

  /**
   * Only entries in the set will be selected, entries not in the set will be unselected even if they are currently selected
   */
  @Provide()
  public claimEntrySetSelected(entries: TableEntry[]) {
    const toSelectKeys = entries.map(e => e._key);
    const toAddSet = new Set(toSelectKeys);

    if (this.selectionMode === 'single') {
      toSelectKeys.splice(1);
    }

    for (const key of toSelectKeys) {
      if (this.selectedSet.has(key)) {
        this.selectedSet.delete(key);
        toAddSet.delete(key);
      }
    }

    this.selectedSet.forEach((value) => {
      this.$emit('entry-selecting', { entryKey: value, selecting: false } as TableEntrySelectingEventData);
    });
    toAddSet.forEach((value) => {
      this.$emit('entry-selecting', { entryKey: value, selecting: true } as TableEntrySelectingEventData);
    });

    this.selectedSet = new Set(toSelectKeys);
    this.shadowSelectedArr = toSelectKeys;
  }

  /**
   * This will **try** to set the most recent regular selected entry
   */
  @Provide()
  private claimMostRecentRegularSelectedEntry(entry: TableEntry, event: MouseEvent | null = null) {
    if (!this.effectiveHoldingSelecting) { return false; }
    if (!event) { return; }
    if (
      !this.mostRecentRegularSelectedEntry ||
      !event.shiftKey
    ) {
      this.mostRecentRegularSelectedEntry = entry;
      return true;
    }
    if (!this.selectedSet.has(this.mostRecentRegularSelectedEntry._key)) {
      this.mostRecentRegularSelectedEntry = entry;
      return true;
    }
    return false;
  }

  @Provide()
  private claimEntrySelectedRangeEnd(entry: TableEntry) {
    if (!this.effectiveHoldingSelecting || !this.mostRecentRegularSelectedEntry) { return; }
    const checkSet = new Set<string>([entry._key, this.mostRecentRegularSelectedEntry._key]);
    const toSelectEntries: TableEntry[] = [];

    utilService.traverseTableData(this.effectiveData, (entry) => {
      if (entry._selectable === false) { return; }
      if (checkSet.size < 1) { return; }

      if (checkSet.has(entry._key)) {
        checkSet.delete(entry._key);
        if (checkSet.size < 1) {
          toSelectEntries.push(entry);
        }
      }

      if (checkSet.size === 1) {
        toSelectEntries.push(entry);
      }
    });

    this.claimEntrySetSelected(toSelectEntries);
  }

  @Provide()
  private claimNestedEntrySelected(params: TableEntrySelectingEventData) {
    /** simply bubble up this event */
    this.$emit('entry-selecting', params);
  }

  @Provide()
  public claimSelectAllEntries(selectAll: boolean) {
    /** NOTE: "select all" is regarding entries in current effective data */
    if (!selectAll) {
      this.selectedSet.forEach((entryKey) => {
        this.$emit('entry-selecting', { entryKey, selecting: false } as TableEntrySelectingEventData);
      });
      this.selectedSet.clear();
    } else {
      const tempSet = new Set(this.selectedSet);
      this.selectedSet.clear();

      utilService.traverseTableData(this.effectiveData, (entry) => {
        if (entry._selectable === false) { return; }
        if (!tempSet.has(entry._key)) {
          this.$emit('entry-selecting', { entryKey: entry._key, selecting: true } as TableEntrySelectingEventData);
        }
        tempSet.delete(entry._key);
        this.selectedSet.add(entry._key);
      });
      if (tempSet.size > 0) {
        tempSet.forEach((entryKey) => {
          this.$emit('entry-selecting', { entryKey, selecting: false } as TableEntrySelectingEventData);
        });
      }
    }
    this.shadowSelectedArr = Array.from(this.selectedSet);
  }

  @Provide()
  public claimEntryExpanded(entry: TableEntry, expanded: boolean, finalize: boolean = true) {
    const cachedSelectedArr = this.shadowSelectedArr.slice(0);

    if (entry._expandable === false && expanded) {
      // console.warn(`Tried to expand entry [${entry._key}] not expandable, which is not allowed...`);
      return;
    }

    if (expanded) {
      if ((this.expandMode === 'in-row' && this.accordion)
        || (this.expandMode === 'slide')) {
        this.expandedSet.clear();
      }
      this.expandedSet.add(entry._key);
    } else {
      this.expandedSet.delete(entry._key);
    }
    this.$emit('entry-expanding', { entryKey: entry._key, expanding: expanded } as TableEntryExpandingEventData);
    if (finalize) {
      this.shadowExpandedArr = Array.from(this.expandedSet);

      /** sorry, for now, stupid way to sync */
      if (this.expandMode === 'slide' && this.syncSlideExpandToSelection) {
        cachedSelectedArr.forEach((s) => {
          if (!this.shadowExpandedArr.includes(s)) {
            const entry = utilService.searchEntry(this.tableData, s);
            if (entry) {
              this.claimEntrySelected(entry, false);
            }
          }
        });
        this.shadowExpandedArr.forEach((e) => {
          if (!cachedSelectedArr.includes(e)) {
            const entry = utilService.searchEntry(this.tableData, e);
            if (entry && entry._selectable !== false) {
              this.claimEntrySelected(entry, true);
            }
          }
        });
      }
    }
  }

  public toggleEntryExpanded(entryKey: string, toExpand?: boolean) {
    const eventData: InternalExpandEventData = {
      entryKey,
      toExpand,
    };
    utilService.eventbus.next({
      type: EVENT_TYPE.EXPAND,
      value: eventData,
    });
  }

  @Provide()
  private claimNestedEntryExpanded(params: TableEntryExpandingEventData) {
    /** simply bubble up this event */
    this.$emit('entry-expanding', params);
  }

  private claimExpandAllEntries(expandAll: boolean) {
    /** NOTE: "expand all" is regarding entries in current effective data */
    if (!expandAll) {
      this.expandedSet.forEach((entryKey) => {
        this.$emit('entry-expanding', { entryKey, expanding: false } as TableEntryExpandingEventData);
      });
      this.expandedSet.clear();
    } else {
      const tempSet = new Set(this.expandedSet);
      this.expandedSet.clear();

      utilService.traverseTableData(this.effectiveData, (entry) => {
        if (entry._expandable === false) { return; }
        if (!tempSet.has(entry._key)) {
          this.$emit('entry-expanding', { entryKey: entry._key, expanding: true } as TableEntryExpandingEventData);
        }
        tempSet.delete(entry._key);
        this.expandedSet.add(entry._key);
      });
      if (tempSet.size > 0) {
        tempSet.forEach((entryKey) => {
          this.$emit('entry-expanding', { entryKey, expanding: false } as TableEntryExpandingEventData);
        });
      }
    }
    this.shadowExpandedArr = Array.from(this.expandedSet);
  }

  @Provide()
  private claimBodyMetrics(metrics: Partial<TableBodyMetrics>) {
    const {
      left, top, width, clientWidth, height, clientHeight,
    } = metrics;
    if (left !== undefined) {
      if (left !== this.bodyMetrics.left) {
        /** for performance, have to do this synchronously instead of wait for reactive watcher in the header component */
        if (this.$refs.header && left !== null) {
          (this.$refs.header as SwTableHeader).updatePosition({ left });
        }
      }
      this.bodyMetrics.left = left;
    }
    if (top !== undefined) {
      if (top !== this.bodyMetrics.top && top !== null) {
        /** again for performance */
        if (this.$refs.fixedPortionLeft) { (this.$refs.fixedPortionLeft as SwTableFixedPortion).updateBodyPosition({ top }); }
        if (this.$refs.fixedPortionRight) { (this.$refs.fixedPortionRight as SwTableFixedPortion).updateBodyPosition({ top }); }
        // (this.$refs.body as SwTableBody).updatePosition({ top });
      }
      this.bodyMetrics.top = top;
    }
    if (width !== undefined) { this.bodyMetrics.width = width; }
    if (clientWidth !== undefined) { this.bodyMetrics.clientWidth = clientWidth; }
    if (height !== undefined) { this.bodyMetrics.height = height; }
    if (clientHeight !== undefined) { this.bodyMetrics.clientHeight = clientHeight; }
  }

  @Provide()
  private claimSortRequest(colKey: string, type: 'cancel' | 'asc' | 'desc') {
    const request: ColumnSortRequestData = { colKey };
    if (type === 'cancel') {
      request.cancel = true;
    } else {
      request.ascending = (type === 'asc');
    }
    this.$emit('request-sort', request);
  }

  private claimChangePagination(type: 'page-number', value: number, isTransient: PageNumberChangeRequestData['isTransient']): void;
  private claimChangePagination(type: 'page-size', value: number): void;
  @Provide()
  private claimChangePagination(type: 'page-number' | 'page-size', value: number, isTransient?: PageNumberChangeRequestData['isTransient']) {
    if (type === 'page-number') {
      this.$emit('request-change-page-number', { isTransient, pageNumber: value } as PageNumberChangeRequestData);
    } else if (type === 'page-size') {
      this.$emit('request-change-page-size', { pageSize: value } as PageSizeChangeRequestData);
    }
  }

  @Provide()
  private claimColResizerTrigger(triggerInfo: ClaimedColumnResizerTrigger) {
    const {
      rect, col, colRect, relatedTarget,
    } = triggerInfo;
    if (this.isTriggerLocked) { return; }
    if (rect === null && relatedTarget && relatedTarget === (this.$refs.colResizer as Vue).$el) { return; }
    this.showResizer = !!rect;
    if (this.showResizer && col && colRect) {
      this.resizerTrigger = Object.freeze({
        col,
        colRect,
        rect: {
          left: rect ? rect.left : 0,
          right: rect ? rect.right : 0,
          top: rect ? rect.top : 0,
          bottom: rect ? rect.bottom : 0,
          width: rect ? rect.width : 0,
          height: rect ? rect.height : 0,
        },
      });
    }
  }

  @Provide()
  private claimColResizerTriggerLock(locked: boolean) {
    this.isTriggerLocked = locked;
  }

  @Provide()
  private claimColResizerDone(adjustedWidth: number, doResize: boolean) {
    this.isTriggerLocked = false;
    this.showResizer = false;
    if (doResize) {
      if (!this.resizerTrigger || !this.resizerTrigger.col || !adjustedWidth) { return; }
      this.$emit(
        'request-adjust-columns',
        utilService.calcColumnAdjustment({
          columnsConfig: this.adjustedColumnsConfig,
          adjustedColumns: this.adjustedColumns || {},
          targetCol: this.resizerTrigger.col,
          toWidth: adjustedWidth,
        }),
      );
    }
  }

  /**
   * request to move `entry` to be before `targetEntry`.
   * If `targetEntry` is null, it means moving to bottom.
   */
  @Provide()
  private claimMoveEntry(entry: TableEntry, targetEntry: TableEntry | null, targetGroup?: TableEntryGroup) {
    this.$emit('request-move-entry', { entry, targetEntry, targetGroup } as TableEntryMoveRequestData);
  }

  @Provide()
  private claimGroupCollapseRequest(group: TableEntryGroup, collapsing: boolean) {
    this.$emit('request-group-collapse', { collapsing, groupKey: group.key } as GroupCollapseRequestData);
  }

  @Provide()
  private claimCellClicked(entry: TableEntry, col: TableColumnConfig, clickEvent?: MouseEvent) {
    this.$emit('cell-click', { entryKey: entry._key, colKey: col.key, event: clickEvent } as TableCellClickEventData);
  }

  @Watch('effectiveData')
  private onEffectiveDataChange() {
    /** try to retain the selected and expanded state for entries that were selected and expanded by _key */
    const tempSelectedSet = new Set(this.selectedSet);
    const tempExpandedSet = new Set(this.expandedSet);
    this.expandedSet.clear();
    this.selectedSet.clear();

    utilService.traverseTableData(this.effectiveData, (entry) => {
      if (tempSelectedSet.has(entry._key)) {
        this.selectedSet.add(entry._key);
        tempSelectedSet.delete(entry._key);
      }
      if (tempExpandedSet.has(entry._key)) {
        this.expandedSet.add(entry._key);
        tempExpandedSet.delete(entry._key);
      }
    });

    this.shadowExpandedArr = Array.from(this.expandedSet);
    this.shadowSelectedArr = Array.from(this.selectedSet);

    tempSelectedSet.forEach((entryKey) => {
      this.$emit('entry-selecting', { entryKey, selecting: false } as TableEntrySelectingEventData);
    });
    tempExpandedSet.forEach((entryKey) => {
      this.$emit('entry-expanding', { entryKey, expanding: false } as TableEntryExpandingEventData);
    });
  }

  @Watch('requestLoadMoreMinNum')
  private onRequestLoadMoreMinNumChange() {}
  @Watch('internalRequestLoadMoreMinNumRecalcCount')
  private onInternalRequestLoadMoreMinNumRecalcCountChange() {
    const minNum = this.requestLoadMoreMinNum;
    if (minNum < 0) {
      this.throttledInfiniteScrollRequest.cancel();
      return;
    }
    window.requestAnimationFrame(() => {
      this.throttledInfiniteScrollRequest(minNum);
    });
  }

  private handleTableResize() {
    (this.$refs.body as SwTableBody).shortDelayReportBodyMetrics();
  }

  private updateHeaderMetrics() {
    const headerRef = this.$refs.header as Vue;
    Object.assign(this.headerMetrics, {
      clientHeight: headerRef ? headerRef.$el.clientHeight : 0,
    });
  }

  get throttledInfiniteScrollRequest() {
    return _debounce(
      (minNum: number) => {
        this.$emit('request-load-more', minNum);
      },
      120,
    );
  }

  private created() {
    /** only entries from effectiveData can be selected initially */
    if (Array.isArray(this.initSelected)) {
      const selectedEntries: TableEntry[] = [];
      utilService.traverseTableData(this.effectiveData, (e) => {
        if (this.initSelected!.includes(e._key)) {
          selectedEntries.push(e);
        }
      });
      selectedEntries.forEach((e) => {
        this.claimEntrySelected(e, true);
      });
    }
  }

  private mounted() {
    this.$nextTick(() => {
      this.updateHeaderMetrics();
      domService.resizeObserver.on(this.$refs.sizeSensor as HTMLElement, this.handleTableResize);
      if (this.$refs.header) {
        // NOTE: Disable this for now for performance
        // this.headerObserver = domService.mutationObserverManager.setup(
        //   (this.$refs.header as Vue).$el,
        //   { childList: true, subtree: true, attributes: true, characterData: true },
        //   _.throttle(this.updateHeaderMetrics, 200),
        // );
      }
    });
  }

  private beforeDestroy() {
    domService.resizeObserver.off(this.$refs.sizeSensor as HTMLElement, this.handleTableResize);
    if (this.headerObserver) {
      this.headerObserver.disconnect();
    }
  }
}
